import React from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";

const STEPS = [
  {
    n: "1",
    title: "Crear / guardar brief",
    text: "Pulsa «+ Nuevo encargo». Elige cliente/ficha, tipo (propuestas, siluetas…), temporada, título, brief y constraints. Guarda.",
  },
  {
    n: "2",
    title: "Lanzar propuestas",
    text: "El botón principal prepara la carpeta Drive (si hace falta) y genera N previews fotorealistas on-model (gpt-image). Por defecto no sube todo a Drive.",
  },
  {
    n: "3",
    title: "Previsualizar en galería",
    text: "Cuando el estado pase a «Pendiente revisión», marca las propuestas que valen. Opcional: «Subir todo al terminar» solo para pruebas rápidas.",
  },
  {
    n: "4",
    title: "Guardar en Drive",
    text: "«Guardar seleccionadas» o «Guardar todas» sube a la carpeta del encargo. «Abrir carpeta Drive» para revisar con el cliente.",
  },
  {
    n: "5",
    title: "Cerrar",
    text: "Cuando el director lo dé por bueno, pulsa «Hecha». El detalle técnico (prompt) queda colapsado abajo.",
  },
];

export default function SkillAgentTasksHelpDialog({ open, onClose }) {
  const theme = useTheme();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <HelpOutlineOutlinedIcon color="primary" />
          <Typography variant="h6" fontWeight={800}>
            Cómo usar Tareas de agentes
          </Typography>
        </Stack>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 0.75, fontWeight: 400 }}
        >
          Es el puesto del director: encargos concretos al agente (como a un
          diseñador senior). No confundir con las <b>Fichas</b>, donde Diseño
          enseña el ADN de la marca.
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {STEPS.map((s) => (
            <Stack
              key={s.n}
              direction="row"
              spacing={1.5}
              alignItems="flex-start"
            >
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: "50%",
                  flexShrink: 0,
                  display: "grid",
                  placeItems: "center",
                  bgcolor: alpha(theme.palette.primary.main, 0.12),
                  color: "primary.main",
                  fontWeight: 800,
                  fontSize: "0.85rem",
                }}
              >
                {s.n}
              </Box>
              <Box>
                <Typography fontWeight={800} variant="body2">
                  {s.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {s.text}
                </Typography>
              </Box>
            </Stack>
          ))}

          <Divider />

          <Box>
            <Typography fontWeight={800} variant="body2" gutterBottom>
              Árbol y Drive
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Izquierda: <b>Temporada → Cliente → Encargo</b> (toggle «Por
              cliente»). En Drive se crea la misma ruta bajo{" "}
              <code>Agentes-STAGE/temporadas/…</code>.
            </Typography>
            <Typography fontWeight={800} variant="body2" gutterBottom>
              Chips de arriba
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              <b>Drive listo</b> = puede crear carpetas. <b>Modo manual</b> = el
              CRM aún no lanza el agente solo (falta conectar Cursor SDK).
              «Preparar» genera prompt + carpeta; «Copiar» es solo para pruebas
              a mano. El objetivo es un botón «Lanzar agente» desde esta misma
              pantalla.
            </Typography>
            <Typography fontWeight={800} variant="body2" gutterBottom>
              Skills publicados
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Si pone «Sin skill publicado», el prompt es genérico. Cuando
              Diseño termine la ficha y Miguel publique el skill, el prompt
              llevará la versión (v1, v2…) con el ADN de marca.
            </Typography>
          </Box>

          <Box
            sx={{
              p: 1.5,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.info.main, 0.08),
              border: "1px solid",
              borderColor: alpha(theme.palette.info.main, 0.2),
            }}
          >
            <Typography variant="body2" fontWeight={700} gutterBottom>
              Probar ahora
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Abre un encargo en <b>Borrador</b> → <b>Preparar encargo</b>{" "}
              (prompt + carpeta Drive). Mientras esté en modo manual, usa{" "}
              <b>Copiar</b> solo si quieres probar el texto en Cursor a mano.
              Cuando conectemos el SDK, el mismo botón lanzará el agente.
            </Typography>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={onClose} variant="contained">
          Entendido
        </Button>
      </DialogActions>
    </Dialog>
  );
}
